// src/utils/sessionPreflight.js

import { getTurnCredentials } from "./getTurnCredentials";

/**
 * Checks whether a live session can actually work, before anyone is called.
 *
 * The failures worth catching here are the ones that otherwise surface as an
 * awkward silence on a paid call: a camera the browser will not release, a
 * blocked microphone permission, or a TURN relay that is unreachable so the
 * connection never completes for anyone behind a corporate NAT. Each is cheap
 * to test in isolation and expensive to diagnose live.
 */

export const CHECK_STATUS = {
  PASS: "pass",
  WARN: "warn",
  FAIL: "fail",
};

const DEVICE_ERROR_MESSAGES = {
  NotAllowedError:
    "Permission was denied. Allow camera and microphone access in your browser, then run the check again.",
  NotFoundError: "No camera or microphone was found on this device.",
  NotReadableError:
    "Your camera or microphone is in use by another application. Close it and run the check again.",
  OverconstrainedError: "No device matched the requested settings.",
};

async function checkMediaDevices() {
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      id: "devices",
      label: "Camera and microphone",
      status: CHECK_STATUS.FAIL,
      detail: "This browser does not support live sessions.",
    };
  }

  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
  } catch (error) {
    // Audio alone is enough to run a useful session, so a camera-only failure
    // is a warning rather than a hard stop.
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return {
        id: "devices",
        label: "Camera and microphone",
        status: CHECK_STATUS.WARN,
        detail:
          "Microphone works, but the camera is unavailable. You can still run an audio and screen-share session.",
      };
    } catch (audioError) {
      return {
        id: "devices",
        label: "Camera and microphone",
        status: CHECK_STATUS.FAIL,
        detail:
          DEVICE_ERROR_MESSAGES[audioError.name] ||
          audioError.message ||
          "Your camera and microphone could not be accessed.",
      };
    }
  }

  const hasAudio = stream.getAudioTracks().length > 0;
  const hasVideo = stream.getVideoTracks().length > 0;
  stream.getTracks().forEach((track) => track.stop());

  if (!hasAudio) {
    return {
      id: "devices",
      label: "Camera and microphone",
      status: CHECK_STATUS.FAIL,
      detail: "No microphone track was available.",
    };
  }

  return {
    id: "devices",
    label: "Camera and microphone",
    status: hasVideo ? CHECK_STATUS.PASS : CHECK_STATUS.WARN,
    detail: hasVideo
      ? "Both devices are available."
      : "Microphone only — no camera detected.",
  };
}

function checkScreenShare() {
  const supported = Boolean(navigator.mediaDevices?.getDisplayMedia);
  return {
    id: "screen",
    label: "Screen sharing",
    // Not gathered here on purpose: getDisplayMedia always prompts, and a
    // permission dialog during a background check is worse than not knowing.
    status: supported ? CHECK_STATUS.PASS : CHECK_STATUS.WARN,
    detail: supported
      ? "Supported by this browser."
      : "This browser cannot share a screen. Chrome, Edge or Firefox on desktop can.",
  };
}

/**
 * Gathers a relay candidate to prove TURN is genuinely reachable.
 *
 * Fetching credentials successfully is not the same as being able to use them;
 * the relay itself can be blocked by the network the user is on. Waiting for an
 * actual `relay` candidate is the only reliable signal.
 */
async function checkRelay(timeoutMs = 8000) {
  const credentials = await getTurnCredentials();

  if (credentials.turnStatus !== "available") {
    return {
      id: "relay",
      label: "Connection relay",
      status: CHECK_STATUS.FAIL,
      detail:
        "TURN relay credentials are unavailable, so the session will fail for anyone on a restricted network.",
      meta: { reason: credentials.reason },
    };
  }

  let pc;
  try {
    pc = new RTCPeerConnection({ iceServers: credentials.iceServers });
    pc.createDataChannel("preflight");

    const relayFound = await new Promise((resolve) => {
      const timer = setTimeout(() => resolve(false), timeoutMs);

      pc.onicecandidate = (event) => {
        if (!event.candidate) return;
        if (event.candidate.candidate.includes(" typ relay")) {
          clearTimeout(timer);
          resolve(true);
        }
      };

      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .catch(() => {
          clearTimeout(timer);
          resolve(false);
        });
    });

    return {
      id: "relay",
      label: "Connection relay",
      status: relayFound ? CHECK_STATUS.PASS : CHECK_STATUS.WARN,
      detail: relayFound
        ? "A relay path is reachable, so the session will connect on restricted networks."
        : "No relay path was found within a few seconds. The session may still work, but could fail behind a strict firewall.",
      meta: { iceServerCount: credentials.iceServerCount },
    };
  } catch (error) {
    return {
      id: "relay",
      label: "Connection relay",
      status: CHECK_STATUS.FAIL,
      detail: error.message || "The relay check could not run.",
    };
  } finally {
    if (pc) pc.close();
  }
}

/** Runs every pre-flight check. Never throws. */
export async function runSessionPreflight() {
  const [devices, relay] = await Promise.all([
    checkMediaDevices(),
    checkRelay(),
  ]);

  const checks = [devices, checkScreenShare(), relay];
  const blocked = checks.some((check) => check.status === CHECK_STATUS.FAIL);

  return {
    checks,
    /** True when a session should not be attempted at all. */
    blocked,
    ranAt: Date.now(),
  };
}
